import { Truck, Clock, MapPin, Package, IndianRupee, AlertTriangle, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

export default function ShippingPage() {
  const { language } = useAuth();

  const deliveryInfo = [
    {
      icon: MapPin,
      title: language === 'hi' ? 'डिलीवरी क्षेत्र' : 'Delivery Areas',
      content:
        language === 'hi'
          ? 'वर्तमान में हम शामली और आसपास के क्षेत्रों में डिलीवरी करते हैं। डिलीवरी के लिए उपलब्ध पिनकोड जांचने के लिए अपना पिनकोड दर्ज करें।'
          : 'Currently we deliver in Shamli and surrounding areas. Enter your pincode to check if delivery is available.',
      highlight:
        language === 'hi'
          ? 'मुख्य क्षेत्र: शामली (247776), कैराना, थाना भवन'
          : 'Main areas: Shamli (247776), Kairana, Thana Bhawan',
    },
    {
      icon: Clock,
      title: language === 'hi' ? 'डिलीवरी का समय' : 'Delivery Timing',
      content:
        language === 'hi'
          ? 'आमतौर पर ऑर्डर उसी दिन या अगले दिन डिलीवर किए जाते हैं।'
          : 'Orders are usually delivered on the same day or next day.',
      details: [
        {
          label: language === 'hi' ? 'सुबह का स्लॉट' : 'Morning Slot',
          value: language === 'hi' ? '8:00 AM - 12:00 PM' : '8:00 AM - 12:00 PM',
        },
        {
          label: language === 'hi' ? 'दोपहर का स्लॉट' : 'Afternoon Slot',
          value: language === 'hi' ? '2:00 PM - 6:00 PM' : '2:00 PM - 6:00 PM',
        },
        {
          label: language === 'hi' ? 'शाम का स्लॉट' : 'Evening Slot',
          value: language === 'hi' ? '6:00 PM - 9:00 PM' : '6:00 PM - 9:00 PM',
        },
      ],
    },
    {
      icon: IndianRupee,
      title: language === 'hi' ? 'डिलीवरी शुल्क' : 'Delivery Charges',
      content:
        language === 'hi'
          ? 'डिलीवरी शुल्क ऑर्डर की राशि पर निर्भर करता है।'
          : 'Delivery charges depend on order amount.',
      pricing: [
        {
          condition: language === 'hi' ? '₹500 से अधिक ऑर्डर' : 'Orders above ₹500',
          charge: language === 'hi' ? 'मुफ्त डिलीवरी' : 'Free Delivery',
          highlight: true,
        },
        {
          condition: language === 'hi' ? '₹500 से कम ऑर्डर' : 'Orders below ₹500',
          charge: '₹30',
          highlight: false,
        },
      ],
    },
  ];

  const importantNotes = language === 'hi'
    ? [
        'डिलीवरी के समय कोई घर पर होना आवश्यक है',
        'डिलीवरी पार्टनर आने से पहले कॉल करेगा',
        'गलत पता देने पर ऑर्डर कैंसिल हो सकता है',
        'खराब मौसम में डिलीवरी में देरी हो सकती है',
        'त्योहारों के दौरान डिलीवरी में अधिक समय लग सकता है',
      ]
    : [
        'Someone must be present at the time of delivery',
        'Delivery partner will call before arriving',
        'Order may be cancelled for incorrect address',
        'Delivery may be delayed in bad weather',
        'Delivery may take longer during festivals',
      ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shipping-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-600 to-green-700 text-white py-16">
        <div className="container-app">
          <div className="flex items-center gap-3 mb-4">
            <Truck className="w-10 h-10" />
            <h1 className="text-3xl md:text-4xl font-bold">
              {language === 'hi' ? 'शिपिंग और डिलीवरी' : 'Shipping & Delivery'}
            </h1>
          </div>
          <p className="text-green-100 text-lg max-w-2xl">
            {language === 'hi'
              ? 'शामली और आसपास के क्षेत्रों में तेज़ और विश्वसनीय डिलीवरी।'
              : 'Fast and reliable delivery in Shamli and surrounding areas.'}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container-app">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Delivery Info Cards */}
            {deliveryInfo.map((info, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <info.icon className="w-6 h-6 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900">{info.title}</h2>
                </div>
                <p className="text-slate-600 mb-4">{info.content}</p>

                {info.highlight && (
                  <div className="bg-green-50 rounded-lg p-3 text-green-700 text-sm font-medium">
                    {info.highlight}
                  </div>
                )}

                {info.details && (
                  <div className="grid sm:grid-cols-3 gap-4 mt-4">
                    {info.details.map((detail, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-sm text-slate-500">{detail.label}</p>
                        <p className="font-semibold text-slate-900">{detail.value}</p>
                      </div>
                    ))}
                  </div>
                )}

                {info.pricing && (
                  <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    {info.pricing.map((price, idx) => (
                      <div
                        key={idx}
                        className={`rounded-lg p-4 ${
                          price.highlight
                            ? 'bg-green-50 border-2 border-green-200'
                            : 'bg-slate-50'
                        }`}
                      >
                        <p className="text-slate-600">{price.condition}</p>
                        <p
                          className={`text-xl font-bold ${
                            price.highlight ? 'text-green-600' : 'text-slate-900'
                          }`}
                        >
                          {price.charge}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Order Tracking */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900">
                  {language === 'hi' ? 'ऑर्डर ट्रैकिंग' : 'Order Tracking'}
                </h2>
              </div>
              <p className="text-slate-600 mb-4">
                {language === 'hi'
                  ? 'आप अपने ऑर्डर की स्थिति "मेरे ऑर्डर" सेक्शन में देख सकते हैं। हर स्टेटस अपडेट पर आपको ईमेल नोटिफिकेशन भी मिलेगा।'
                  : 'You can check your order status in the "My Orders" section. You will also receive email notifications for every status update.'}
              </p>
              <div className="flex flex-wrap gap-2">
                {['Pending', 'Confirmed', 'Processing', 'Shipped', 'Delivered'].map((status, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-slate-100 rounded-full text-sm text-slate-600"
                  >
                    {idx + 1}. {status}
                  </span>
                ))}
              </div>
            </div>

            {/* Important Notes */}
            <div className="bg-amber-50 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
                <h2 className="text-xl font-semibold text-slate-900">
                  {language === 'hi' ? 'महत्वपूर्ण जानकारी' : 'Important Notes'}
                </h2>
              </div>
              <ul className="space-y-2">
                {importantNotes.map((note, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-slate-700">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 flex-shrink-0" />
                    {note}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact for Delivery Issues */}
            <div className="bg-green-50 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Phone className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-semibold text-slate-900">
                  {language === 'hi' ? 'डिलीवरी संबंधी समस्या?' : 'Delivery Issues?'}
                </h2>
              </div>
              <p className="text-slate-600 mb-4">
                {language === 'hi'
                  ? 'यदि आपको डिलीवरी में कोई समस्या है या आपका ऑर्डर देरी से आ रहा है, तो कृपया हमसे संपर्क करें।'
                  : 'If you have any delivery issues or your order is delayed, please contact us.'}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact">
                  <Button className="bg-green-600 hover:bg-green-700">
                    {language === 'hi' ? 'संपर्क करें' : 'Contact Us'}
                  </Button>
                </Link>
                <a href="https://wa.me/919999999999" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-green-600 text-green-600">
                    {language === 'hi' ? 'WhatsApp करें' : 'WhatsApp Us'}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
